import { NotificationResponseDto } from '@app/api';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import { useNotificationDescription } from '@app/hooks/useNotificationDescription';
import { useToggle } from '@app/hooks/useToggle';
import { BreakpointsEnum } from '@app/types';
import { ArrowDown, ArrowUp } from '@app/ui/iconComponents';
import { FC } from 'react';
import styled from 'styled-components';
import { isCollapsableByEvent } from '../model/helpers';

interface Props {
  notification: NotificationResponseDto;
  className?: string;
}

const NotificationItem: FC<Props> = ({ className, notification }) => {
  const isCollapsable = isCollapsableByEvent(notification.event);
  const { title, description, date } = useNotificationDescription(notification);
  const [isOpenedDescription, , , toggleDescription] = useToggle();

  return (
    <Root
      className={className}
      onClick={isCollapsable ? toggleDescription : undefined}
      $isCollapsable={isCollapsable}
    >
      <Top>
        <Title>{title}</Title>
        <>
          {isCollapsable && (
            <>
              {isOpenedDescription ? (
                <ArrowUp
                  width={12}
                  height={7}
                />
              ) : (
                <ArrowDown />
              )}
            </>
          )}
        </>
      </Top>
      <ContentWrap {...(isCollapsable && { 'aria-expanded': isOpenedDescription })}>
        <>
          {isCollapsable ? (
            <>{isOpenedDescription && <DescriptionText>{description}</DescriptionText>}</>
          ) : (
            <DescriptionText>{description}</DescriptionText>
          )}
        </>
        <CreatedDate>{date}</CreatedDate>
      </ContentWrap>
    </Root>
  );
};

interface RootStyleProps {
  $isCollapsable: boolean;
}

const Root = styled.div<RootStyleProps>`
  padding-bottom: 16px;
  padding-top: 16px;
  border-bottom: 1px solid ${COLORS.grayscale100};
  cursor: ${({ $isCollapsable }) => $isCollapsable && 'pointer'}; ;
`;

const Title = styled.p`
  ${TYPOGRAPHY.footnoteMedium13};
  color: ${COLORS.grayscale700};
`;

const ContentWrap = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DescriptionText = styled.div`
  ${TYPOGRAPHY.disclaimerRegular12};
  color: ${COLORS.grayscale500};
  margin-right: 10px;
  @media (max-width: ${BreakpointsEnum.sm}px) {
    ${TYPOGRAPHY.body1Regular14};
  }
`;

const CreatedDate = styled.p`
  ${TYPOGRAPHY.disclaimerRegular12};
  color: ${COLORS.grayscale300};
  @media (max-width: ${BreakpointsEnum.sm}px) {
    ${TYPOGRAPHY.body1Regular14};
  }
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 16px;
`;

export default NotificationItem;
